/** Number formatting for the context meter. */

/** One decimal, with a trailing `.0` dropped: `1.1`, but `1` rather than `1.0`. */
function oneDecimal(value: number): string {
  const text = value.toFixed(1);
  return text.endsWith('.0') ? text.slice(0, -2) : text;
}

/**
 * A token count at display precision: `937`, `131.4k`, `1M`. Negative or
 * non-finite input gives `—`.
 *
 * Each rung rounds to the precision it will print BEFORE deciding whether the
 * value still fits, so a value that rounds up across the boundary moves up a
 * unit instead of printing as `1000k`. `M` is the top rung and never divides
 * again: 1e9 is `1000M`.
 */
export function formatTokens(value: number): string {
  if (!Number.isFinite(value) || value < 0) return '—';

  const whole = Math.round(value);
  if (whole < 1000) return String(whole);

  const inK = Number((value / 1_000).toFixed(1));
  if (inK < 1000) return `${oneDecimal(inK)}k`;

  return `${oneDecimal(Number((value / 1_000_000).toFixed(1)))}M`;
}

/**
 * The hover tooltip:
 *
 *     Context 380k / 1M (38%) · Plan 72%
 *
 * With no reading yet, the context half names the window alone:
 * `Context 128k window · measured from your next message`.
 *
 * `prefix` is the `~` marking a count the gateway reconstructed rather than
 * measured, and applies to both the figure and the percentage.
 *
 * `planPercent` is the share of a subscription plan's quota consumed, and is
 * omitted — separator and all — when null or non-finite. Nothing supplies it
 * today: models route through LiteLLM to Bedrock on per-customer API keys, and
 * openclaw's quota RPCs answer `providers: []` against our gateway. It stays a
 * parameter so wiring a real source stays a call-site change.
 */
export function formatContextTooltip(params: {
  readonly usedTokens: number | null;
  readonly limitTokens: number;
  readonly percent: number | null;
  readonly prefix: string;
  readonly planPercent?: number | null;
}): string {
  const { usedTokens, limitTokens, percent, prefix, planPercent } = params;
  const context =
    usedTokens == null || percent == null
      ? `Context ${formatTokens(limitTokens)} window · measured from your next message`
      : `Context ${prefix}${formatTokens(usedTokens)} / ${formatTokens(limitTokens)} (${prefix}${percent}%)`;
  return planPercent == null || !Number.isFinite(planPercent)
    ? context
    : `${context} · Plan ${Math.round(planPercent)}%`;
}
